import {
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
} from '@mui/material';
import {
  Button,
  InputText,
  StylovyzeComposableDialog,
  StylovyzeFormV2,
  useGlobalization,
  Text,
  useUnits,
} from '@innovyze/stylovyze';
import { getLengthCostPerDisplayLabel } from '@Utils/lengthUnitCost';
import { FormikHelpers, FormikValues } from 'formik';
import React from 'react';
import { useHistory } from 'react-router-dom';
import { selectGroupSets, useCostTableNames } from '@Selectors';
import { GroupSet } from '@Types/groupSets.types';
import { normaliseCostTableName } from '../../utils/normaliseCostTableName';

interface DefineCostTableDialogProps {
  isOpen: boolean;
  setOpen: (arg0: boolean) => void;
  onCreate: (request: {
    assetGroupSetId: string;
    name: string;
    costPer: string;
  }) => Promise<void>;
}

export const CreateCostTableDialog = ({
  setOpen,
  isOpen,
  onCreate,
}: DefineCostTableDialogProps) => {
  const { t } = useGlobalization();
  const { system } = useUnits();
  const history = useHistory();

  const groupSets: GroupSet[] = selectGroupSets();
  const existingNames: Set<string> = useCostTableNames();

  const availableGroups =
    groupSets
      ?.filter(gs => gs.publishState === 'published')
      .map(gs => {
        const context = [gs.systemType, gs.assetType]
          .filter(Boolean)
          .join(' - ');
        return {
          key: gs._id,
          value: context ? `${gs.name} (${context})` : gs.name,
        };
      }) ?? [];

  const availableCostPerOptions = [
    { key: 'each', value: 'each' },
    { key: 'length', value: getLengthCostPerDisplayLabel(t, system) },
  ];

  const handleSubmit = async (
    values: FormikValues,
    { setSubmitting, resetForm, setFieldError }: FormikHelpers<FormikValues>
  ) => {
    setSubmitting(true);
    try {
      await onCreate({
        assetGroupSetId: values.assetGroupSetId,
        name: values.name.trim(),
        costPer: values.costPer,
      });
      resetForm();
      setOpen(false);
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      if (
        (error as any)?.response?.data?.error === 'Duplicate cost table name'
      ) {
        setFieldError('name', t('A cost table with this name already exists'));
      } else {
        // Non-duplicate errors (network, 500s, etc.) are surfaced by the
        // onCreate prop via a toast before re-throwing.
      }
      setSubmitting(false);
    }
  };

  if (availableGroups.length === 0) {
    const noPublishedSets = groupSets.length > 0;
    return (
      <StylovyzeComposableDialog
        onCancel={() => setOpen(false)}
        open={isOpen}
        title={
          noPublishedSets
            ? t('No Published Asset Group Sets')
            : t('No Asset Group Sets Created')
        }
        footer={
          <>
            <Button
              variant="contained"
              color="secondary"
              onClick={() => setOpen(false)}>
              {t('Close')}
            </Button>
            <Button
              variant="contained"
              color="primary"
              onClick={() => history.push('/agsets')}>
              {noPublishedSets
                ? t('Go to Asset Group Sets')
                : t('Create Asset Group Set')}
            </Button>
          </>
        }>
        <Text>
          {noPublishedSets
            ? t(
                'No Asset Group Sets have been published. ' +
                  'Go to the Groups section and publish one before proceeding.'
              )
            : t(
                'You do not have any Asset Group Sets created. ' +
                  'Please create one before proceeding with the deterioration model.'
              )}
        </Text>
      </StylovyzeComposableDialog>
    );
  }

  return (
    <StylovyzeFormV2
      initialValues={{
        assetGroupSetId: '',
        name: '',
        costPer: '',
      }}
      validateOnMount
      validate={values => {
        const errors: { [key: string]: string } = {};
        if (!values.assetGroupSetId) {
          errors.assetGroupSetId = t('Asset Group Set is required');
        }
        if (!values.name) {
          errors.name = t('Name is required');
        } else if (existingNames.has(normaliseCostTableName(values.name))) {
          errors.name = t('A cost table with this name already exists');
        }
        if (!values.costPer) {
          errors.costPer = t('Cost Per is required');
        }
        return errors;
      }}
      onSubmit={handleSubmit}>
      {({
        submitForm,
        isSubmitting,
        isValid,
        errors,
        resetForm,
        values,
        setFieldValue,
      }) => {
        const cancelHandler = () => {
          resetForm();
          setOpen(false);
        };
        return (
          <StylovyzeComposableDialog
            open={isOpen}
            onCancel={cancelHandler}
            title={t('New Cost Table')}
            dialogProps={{ PaperProps: { sx: { minHeight: '32rem' } } }}
            footer={
              <>
                <Button
                  variant="contained"
                  color="secondary"
                  disabled={isSubmitting}
                  onClick={cancelHandler}>
                  {t('Cancel')}
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={isSubmitting || !isValid}
                  onClick={submitForm}>
                  {isSubmitting ? t('Creating...') : t('Create Cost Table')}
                </Button>
              </>
            }>
            <InputText
              fieldAttrs={{ name: 'name' }}
              textField={{
                label: t('Cost Table Name*'),
                fullWidth: true,
                error: !!errors.name,
                helperText: errors.name ?? ' ',
                disabled: isSubmitting,
              }}
            />
            <FormControl
              fullWidth
              variant="outlined"
              margin="normal"
              error={!!errors.assetGroupSetId}>
              <InputLabel>{t('Asset Group Set*')}</InputLabel>
              <Select
                name="assetGroupSetId"
                value={values.assetGroupSetId}
                onChange={e => setFieldValue('assetGroupSetId', e.target.value)}
                label={t('Asset Group Set*')}
                disabled={isSubmitting}>
                {availableGroups.map(group => (
                  <MenuItem key={group.key} value={group.key}>
                    {group.value}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.assetGroupSetId
                  ? errors.assetGroupSetId.toString()
                  : t(
                      'Only published sets appear here. Go to Groups section to publish.'
                    )}
              </FormHelperText>
              <FormHelperText>
                {errors.assetGroupSetId
                  ? t(
                      'Only published sets appear here. Go to Groups section to publish.'
                    )
                  : ' '}
              </FormHelperText>
            </FormControl>
            <FormControl
              fullWidth
              variant="outlined"
              margin="normal"
              error={!!errors.costPer}>
              <InputLabel>{t('Cost Per*')}</InputLabel>
              <Select
                name="costPer"
                value={values.costPer}
                onChange={e => setFieldValue('costPer', e.target.value)}
                label={t('Cost Per*')}
                disabled={isSubmitting}>
                {availableCostPerOptions.map(opt => (
                  <MenuItem key={opt.key} value={opt.key}>
                    {opt.value}
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText>
                {errors.costPer ? errors.costPer.toString() : ' '}
              </FormHelperText>
            </FormControl>
          </StylovyzeComposableDialog>
        );
      }}
    </StylovyzeFormV2>
  );
};
